/**
 * Media Capture Component
 *
 * Mobile-optimized media capture with:
 * - Camera integration (back camera for inspection photos/videos)
 * - Photo and video capture support
 * - Media preview and management
 * - Caption input
 * - Gallery view for existing media
 */

import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  CircularProgress,
  Paper,
  Chip,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import VideocamIcon from '@mui/icons-material/Videocam';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useNotify } from '@/hooks/useNotify';

export type MediaType = 'photo' | 'video';

export interface MediaItem {
  id: string;
  type: MediaType;
  url: string;
  thumbnailUrl?: string;
  caption?: string;
  uploadedAt?: string;
  durationSeconds?: number;
}

interface MediaCaptureProps {
  media: MediaItem[];
  onCapture?: (file: File, type: MediaType) => Promise<void>;
  onDelete?: (mediaId: string) => Promise<void>;
  onCaptionChange?: (mediaId: string, caption: string) => void;
  disabled?: boolean;
  maxMedia?: number;
  maxVideoSeconds?: number;
  itemName?: string;
  allowVideo?: boolean;
}

export function MediaCapture({
  media,
  onCapture,
  onDelete,
  onCaptionChange,
  disabled = false,
  maxMedia = 10,
  maxVideoSeconds = 30,
  itemName,
  allowVideo = true,
}: MediaCaptureProps) {
  const { t } = useTranslation('inspection');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [previewFile, setPreviewFile] = useState<{
    file: File;
    url: string;
    type: MediaType;
  } | null>(null);
  const [caption, setCaption] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [deleteMediaId, setDeleteMediaId] = useState<string | null>(null);

  const photoInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const canAddMore = media.length < maxMedia;

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, type: MediaType) => {
      const file = event.target.files?.[0];
      if (file && onCapture) {
        const previewUrl = URL.createObjectURL(file);
        setPreviewFile({ file, url: previewUrl, type });
        setCaption('');
      }
      event.target.value = '';
    },
    [onCapture]
  );

  const handleConfirmUpload = async () => {
    if (!previewFile || !onCapture) return;

    setIsUploading(true);
    try {
      await onCapture(previewFile.file, previewFile.type);
      URL.revokeObjectURL(previewFile.url);
      setPreviewFile(null);
      setCaption('');
    } catch {
      notify.error(tc('error'), t('media.uploadFailed'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancelUpload = () => {
    if (previewFile) {
      URL.revokeObjectURL(previewFile.url);
      setPreviewFile(null);
      setCaption('');
    }
  };

  const handleDelete = async (mediaId: string) => {
    if (onDelete) {
      await onDelete(mediaId);
    }
    setSelectedMedia(null);
  };

  const openPhotoCamera = () => {
    photoInputRef.current?.click();
  };

  const openVideoCamera = () => {
    videoInputRef.current?.click();
  };

  const openGallery = () => {
    galleryInputRef.current?.click();
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Box>
      {/* Media Grid */}
      {media.length > 0 && (
        <ImageList
          cols={3}
          gap={8}
          sx={{
            width: '100%',
            mb: 2,
            '& .MuiImageListItem-root': {
              aspectRatio: '1 / 1',
              overflow: 'hidden',
              borderRadius: 1,
            },
          }}
        >
          {media.map((item) => (
            <ImageListItem
              key={item.id}
              onClick={() => setSelectedMedia(item)}
              sx={{
                cursor: 'pointer',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                position: 'relative',
                '&:active': {
                  transform: 'scale(0.98)',
                },
              }}
            >
              {item.type === 'video' ? (
                <>
                  <img
                    src={item.thumbnailUrl || '/video-placeholder.png'}
                    alt={item.caption || t('media.inspectionVideo')}
                    loading="lazy"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                  {/* Video overlay */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                    }}
                  >
                    <PlayCircleIcon
                      sx={{
                        fontSize: 40,
                        color: 'white',
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
                      }}
                    />
                  </Box>
                  {item.durationSeconds && (
                    <Chip
                      size="small"
                      label={formatDuration(item.durationSeconds)}
                      sx={{
                        position: 'absolute',
                        bottom: 4,
                        right: 4,
                        height: 20,
                        fontSize: '0.65rem',
                        backgroundColor: 'rgba(0,0,0,0.7)',
                        color: 'white',
                      }}
                    />
                  )}
                </>
              ) : (
                <img
                  src={item.thumbnailUrl || item.url}
                  alt={item.caption || t('media.inspectionPhoto')}
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              )}
              {item.caption && (
                <ImageListItemBar
                  title={item.caption}
                  sx={{
                    '& .MuiImageListItemBar-title': {
                      fontSize: '0.65rem',
                    },
                  }}
                />
              )}
            </ImageListItem>
          ))}
        </ImageList>
      )}

      {/* Add Media Buttons */}
      {canAddMore && !disabled && (
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<PhotoCameraIcon />}
            onClick={openPhotoCamera}
            sx={{
              flex: 1,
              minWidth: 100,
              minHeight: 48,
              touchAction: 'manipulation',
            }}
          >
            {t('media.photo')}
          </Button>
          {allowVideo && (
            <Button
              variant="outlined"
              startIcon={<VideocamIcon />}
              onClick={openVideoCamera}
              sx={{
                flex: 1,
                minWidth: 100,
                minHeight: 48,
                touchAction: 'manipulation',
              }}
            >
              {t('media.video')}
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<AddPhotoAlternateIcon />}
            onClick={openGallery}
            sx={{
              flex: 1,
              minWidth: 100,
              minHeight: 48,
              touchAction: 'manipulation',
            }}
          >
            {t('media.gallery')}
          </Button>
        </Box>
      )}

      {/* Empty State */}
      {media.length === 0 && !disabled && (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            textAlign: 'center',
            backgroundColor: 'background.default',
            mt: 2,
          }}
        >
          <PhotoCameraIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {t('media.noMedia')}
          </Typography>
          <Typography variant="caption" color="text.disabled">
            {t('media.captureOrSelectHint')}
          </Typography>
        </Paper>
      )}

      {/* Hidden File Inputs */}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => handleFileSelect(e, 'photo')}
        style={{ display: 'none' }}
      />
      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        capture="environment"
        onChange={(e) => handleFileSelect(e, 'video')}
        style={{ display: 'none' }}
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*,video/*"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            const type: MediaType = file.type.startsWith('video/') ? 'video' : 'photo';
            handleFileSelect(e, type);
          }
        }}
        style={{ display: 'none' }}
      />

      {/* Preview Dialog */}
      <ResponsiveDialog
        mobileTitle={previewFile?.type === 'video' ? t('media.addVideo') : t('media.addPhoto')}
        open={!!previewFile}
        onClose={handleCancelUpload}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            {previewFile?.type === 'video' ? t('media.addVideo') : t('media.addPhoto')}
          </Typography>
          <Tooltip title={tc('close')}>
            <IconButton onClick={handleCancelUpload} size="small" aria-label={tc('close')}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent sx={{ px: 2, pb: 0 }}>
          {previewFile && (
            <>
              {previewFile.type === 'video' ? (
                <Box
                  component="video"
                  src={previewFile.url}
                  controls
                  sx={{
                    width: '100%',
                    maxHeight: 300,
                    borderRadius: 1,
                    backgroundColor: 'grey.900',
                  }}
                />
              ) : (
                <Box
                  component="img"
                  src={previewFile.url}
                  alt={t('media.preview')}
                  sx={{
                    width: '100%',
                    maxHeight: 300,
                    objectFit: 'contain',
                    borderRadius: 1,
                    backgroundColor: 'grey.100',
                  }}
                />
              )}
              {previewFile.type === 'video' && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {t('media.maxSeconds', { seconds: maxVideoSeconds })}
                </Typography>
              )}
              {itemName && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {t('media.item')}: {itemName}
                </Typography>
              )}
              <TextField
                fullWidth
                size="small"
                placeholder={previewFile.type === 'video' ? t('media.videoCaptionPlaceholder') : t('media.photoCaptionPlaceholder')}
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                sx={{ mt: 2 }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button onClick={handleCancelUpload} disabled={isUploading}>
            {tc('cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleConfirmUpload()}
            disabled={isUploading}
            startIcon={isUploading ? <CircularProgress size={16} /> : undefined}
          >
            {isUploading ? tc('loading') : tc('save')}
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      {/* Media Detail Dialog */}
      <ResponsiveDialog
        mobileTitle={selectedMedia?.caption || (selectedMedia?.type === 'video' ? t('media.video') : t('media.photo'))}
        open={!!selectedMedia}
        onClose={() => setSelectedMedia(null)}
        maxWidth="md"
        fullWidth
      >
        {selectedMedia && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
              <Typography variant="h6" sx={{ flex: 1 }}>
                {selectedMedia.caption || (selectedMedia.type === 'video' ? t('media.video') : t('media.photo'))}
              </Typography>
              <Tooltip title={tc('close')}>
                <IconButton onClick={() => setSelectedMedia(null)} size="small" aria-label={tc('close')}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </DialogTitle>
            <DialogContent sx={{ px: 2, pb: 0 }}>
              {selectedMedia.type === 'video' ? (
                <Box
                  component="video"
                  src={selectedMedia.url}
                  controls
                  autoPlay
                  sx={{
                    width: '100%',
                    maxHeight: 'calc(100vh - 250px)',
                    borderRadius: 1,
                    backgroundColor: 'grey.900',
                  }}
                />
              ) : (
                <Box
                  component="img"
                  src={selectedMedia.url}
                  alt={selectedMedia.caption || t('media.photo')}
                  sx={{
                    width: '100%',
                    maxHeight: 'calc(100vh - 250px)',
                    objectFit: 'contain',
                    borderRadius: 1,
                    backgroundColor: 'grey.100',
                  }}
                />
              )}
              {onCaptionChange && (
                <TextField
                  fullWidth
                  size="small"
                  placeholder={selectedMedia.type === 'video' ? t('media.videoCaption') : t('media.photoCaption')}
                  defaultValue={selectedMedia.caption || ''}
                  onBlur={(e) => onCaptionChange(selectedMedia.id, e.target.value)}
                  sx={{ mt: 2 }}
                />
              )}
            </DialogContent>
            <DialogActions sx={{ p: 2, pt: 1, justifyContent: 'space-between' }}>
              {onDelete && (
                <Button
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteMediaId(selectedMedia.id)}
                >
                  {tc('delete')}
                </Button>
              )}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title={tc('fullscreen')}>
                  <IconButton onClick={() => window.open(selectedMedia.url, '_blank')} aria-label={tc('fullscreen')}>
                    <FullscreenIcon />
                  </IconButton>
                </Tooltip>
                <Button variant="contained" onClick={() => setSelectedMedia(null)}>
                  {tc('close')}
                </Button>
              </Box>
            </DialogActions>
          </>
        )}
      </ResponsiveDialog>

      {/* Delete Media Confirmation */}
      <ConfirmDialog
        open={!!deleteMediaId}
        title={tc('delete')}
        message={t('media.confirmDelete')}
        confirmText={tc('delete')}
        confirmColor="error"
        onConfirm={() => {
          if (deleteMediaId) void handleDelete(deleteMediaId);
          setDeleteMediaId(null);
        }}
        onCancel={() => setDeleteMediaId(null)}
      />
    </Box>
  );
}

/**
 * Compact media preview for item rows
 */
interface MediaPreviewProps {
  media: MediaItem[];
  maxVisible?: number;
  onClick?: () => void;
}

export function MediaPreview({ media, maxVisible = 3, onClick }: MediaPreviewProps) {
  if (media.length === 0) return null;

  const visibleMedia = media.slice(0, maxVisible);
  const remaining = media.length - maxVisible;

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      {visibleMedia.map((item, index) => (
        <Box
          key={item.id}
          sx={{
            width: 32,
            height: 32,
            borderRadius: 0.5,
            border: 2,
            borderColor: 'background.paper',
            ml: index > 0 ? -1.5 : 0,
            position: 'relative',
            zIndex: maxVisible - index,
            overflow: 'hidden',
          }}
        >
          <Box
            component="img"
            src={item.thumbnailUrl || item.url}
            alt=""
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
          {item.type === 'video' && (
            <PlayCircleIcon
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: 16,
                color: 'white',
                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))',
              }}
            />
          )}
        </Box>
      ))}
      {remaining > 0 && (
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 0.5,
            backgroundColor: 'grey.200',
            border: 2,
            borderColor: 'background.paper',
            ml: -1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="caption" fontWeight={600}>
            +{remaining}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
